import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { Card, CardContent } from '../../../components/ui/Card';
import Checkbox from '../../../components/ui/Checkbox';
import { API_BASE_URL } from '../../../config/api';
import { documentService, vendorService, getErrorMessage } from '../../../services/apiService';
import { useToast } from '../../../components/ui/Toast';
import { isIndia } from '../../../utils/countries';
// Agreement PDFs are now generated by backend (GET /vendors/:id/agreements/:type/generate)
const AGREEMENT_TYPE_MAP = {
  'nda-basic': 'NDA',
  'sqa-basic': 'SQA',
  '4m-basic': 'FOUR_M',
  'coc-basic': 'CODE_OF_CONDUCT',
  'ca-basic': 'COMPLIANCE_AGREEMENT',
  'sd-basic': 'SELF_DECLARATION',
  'overseas-agreement-basic': 'OVERSEAS_AGREEMENT',
};
const AGREEMENT_FILENAME_MAP = {
  'nda-basic': 'NDA_Agreement.pdf',
  'sqa-basic': 'SA_Agreement.pdf',
  '4m-basic': '4M_Declaration.pdf',
  'coc-basic': 'Code_of_Conduct.pdf',
  'ca-basic': 'Compliance_Agreement.pdf',
  'sd-basic': 'Self_Declaration.pdf',
  'overseas-agreement-basic': 'Overseas_Agreement.pdf',
};

const AGREEMENT_OPTIONS_INDIAN = [
  { value: 'NDA', label: 'Mutual NDA' },
  { value: 'SQA', label: 'Supplier Agreement' },
  { value: 'FOUR_M', label: '4M Declaration' },
  { value: 'CODE_OF_CONDUCT', label: 'Code of Conduct for Business Partners' },
  { value: 'COMPLIANCE_AGREEMENT', label: 'Compliance Agreement' },
  { value: 'SELF_DECLARATION', label: 'Self Declaration' },
];
const AGREEMENT_OPTIONS_FOREIGN = [
  { value: 'NDA', label: 'Mutual NDA' },
  { value: 'FOUR_M', label: '4M Declaration' },
  { value: 'CODE_OF_CONDUCT', label: 'Code of Conduct for Business Partners' },
  { value: 'SELF_DECLARATION', label: 'Self Declaration' },
  { value: 'OVERSEAS_AGREEMENT', label: 'Overseas Agreement' },
];

const AgreementsTab = ({ vendor, onRefresh }) => {
  const toast = useToast();
  const [agreements, setAgreements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Modal states
  const [viewAgreementModal, setViewAgreementModal] = useState({ isOpen: false, agreement: null });
  const [signatureModal, setSignatureModal] = useState({ isOpen: false, agreement: null });
  const [commentModal, setCommentModal] = useState({ isOpen: false, agreement: null });
  const [agreementData, setAgreementData] = useState(null);
  const [signatureData, setSignatureData] = useState(null);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [loadingData, setLoadingData] = useState(false);
  const [isRegenerating, setIsRegenerating] = useState(false);

  // Select agreements modal (when vendor has no signed flags)
  const [showSelectAgreementsModal, setShowSelectAgreementsModal] = useState(false);
  const [selectedAgreementTypes, setSelectedAgreementTypes] = useState([]);
  const [generateWithSignature, setGenerateWithSignature] = useState(true);
  const [isGeneratingAgreements, setIsGeneratingAgreements] = useState(false);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (showSelectAgreementsModal) {
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = ''; };
    }
  }, [showSelectAgreementsModal]);

  useEffect(() => {
    const fetchAgreements = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch detailed agreements from the agreement-details endpoint
        let detailedAgreements = [];
        try {
          const response = await fetch(`${API_BASE_URL}/vendors/${vendor.id}/agreement-details`, {
            credentials: 'include', // Include cookies for authentication
          });

          if (response.ok) {
            const data = await response.json();
            // Handle different response structures
            detailedAgreements = Array.isArray(data) ? data : (data?.data || data?.agreements || []);
          } else if (response.status === 404) {
            // 404 is expected and acceptable - means no detailed agreements exist
            // This is normal, so we silently continue with basic agreements only
          } else {
            // Other errors (500, 403, etc.) are logged but don't block the UI
            console.warn('Failed to fetch detailed agreements:', response.status);
          }
        } catch (fetchError) {
          // Network errors or other fetch failures are logged but don't block the UI
          // We continue with basic agreements even if detailed fetch fails
          console.warn('Error fetching detailed agreements (non-critical):', fetchError);
        }

        // Create basic agreements from vendor registration data
        const basicAgreements = [];
        if (vendor.nda) {
          basicAgreements.push({
            id: 'nda-basic',
            title: 'Mutual Non-Disclosure Agreement (NDA)',
            type: 'Legal',
            status: 'Signed',
            signed_date: vendor.created_at || vendor.registration_date,
            signed_by: vendor.contact_person_name,
            valid_until: 'Perpetual',
            description: 'Mutual Confidentiality and Non-Disclosure Agreement accepted during registration',
            version: '1.0',
            is_basic: true
          });
        }
        if (vendor.sqa) {
          basicAgreements.push({
            id: 'sqa-basic',
            title: 'Supplier Agreement',
            type: 'Quality',
            status: 'Signed',
            signed_date: vendor.created_at || vendor.registration_date,
            signed_by: vendor.contact_person_name,
            valid_until: 'Annual Renewal',
            description: 'Basic SA agreement signed during registration',
            version: '1.0',
            is_basic: true
          });
        }
        if (vendor.four_m) {
          basicAgreements.push({
            id: '4m-basic',
            title: '4M Change Management Agreement',
            type: 'Operational',
            status: 'Signed',
            signed_date: vendor.created_at || vendor.registration_date,
            signed_by: vendor.contact_person_name,
            valid_until: 'Annual Renewal',
            description: '4M Change Management agreement signed during registration',
            version: '1.0',
            is_basic: true
          });
        }
        if (vendor.code_of_conduct) {
          basicAgreements.push({
            id: 'coc-basic',
            title: 'Code of Conduct for Business Partners',
            type: 'Compliance',
            status: 'Signed',
            signed_date: vendor.created_at || vendor.registration_date,
            signed_by: vendor.contact_person_name,
            valid_until: 'Perpetual',
            description: 'Amber Group Code of Conduct for Business Partners (Ver. 1, 2nd Sep 2026) accepted during registration',
            version: '1.0',
            is_basic: true
          });
        }
        if (vendor.compliance_agreement) {
          basicAgreements.push({
            id: 'ca-basic',
            title: 'Compliance Agreement',
            type: 'Compliance',
            status: 'Signed',
            signed_date: vendor.created_at || vendor.registration_date,
            signed_by: vendor.contact_person_name,
            valid_until: 'Annual Renewal',
            description: 'Compliance agreement signed during registration',
            version: '1.0',
            is_basic: true
          });
        }
        if (vendor.self_declaration) {
          basicAgreements.push({
            id: 'sd-basic',
            title: 'Self Declaration',
            type: 'Declaration',
            status: 'Signed',
            signed_date: vendor.created_at || vendor.registration_date,
            signed_by: vendor.contact_person_name,
            valid_until: 'Annual Renewal',
            description: 'Self declaration signed during registration',
            version: '1.0',
            is_basic: true
          });
        }
        if (vendor.overseas_agreement) {
          basicAgreements.push({
            id: 'overseas-agreement-basic',
            title: 'Overseas Agreement',
            type: 'Legal',
            status: 'Signed',
            signed_date: vendor.created_at || vendor.registration_date,
            signed_by: vendor.contact_person_name,
            valid_until: 'Annual Renewal',
            description: 'Overseas Agreement signed during registration',
            version: '1.0',
            is_basic: true
          });
        }

        // Combine basic and detailed agreements
        const allAgreements = [...basicAgreements, ...detailedAgreements];
        setAgreements(allAgreements);

        // Don't set error if no agreements found - that's just an empty state, not an error
        // The UI will show "No agreements found" message naturally
      } catch (err) {
        // Only set error for unexpected failures that prevent us from loading basic agreements
        console.error('Failed to load agreements:', err);
        const errorMsg = getErrorMessage(err) || 'Failed to load agreements. Please try again.';
        setError(errorMsg);
      } finally {
        setLoading(false);
      }
    };

    if (vendor?.id) {
      fetchAgreements();
    }
  }, [vendor?.id]);

  // Auto-poll when agreements are being generated
  useEffect(() => {
    const status = vendor?.agreement_generation_status;
    if (status !== 'PENDING' && status !== 'GENERATING') return;
    const interval = setInterval(() => {
      onRefresh?.();
    }, 7000);
    return () => clearInterval(interval);
  }, [vendor?.agreement_generation_status, onRefresh]);

  const getStatusColor = (status) => {
    switch (status.toLowerCase()) {
      case 'signed':
        return 'bg-emerald-100 text-emerald-800';
      case 'pending signature':
        return 'bg-amber-100 text-amber-800';
      case 'expired':
        return 'bg-red-100 text-red-800';
      case 'under review':
        return 'bg-slate-100 text-slate-800';
      default:
        return 'bg-slate-100 text-slate-800';
    }
  };

  const getTypeColor = (type) => {
    switch (type.toLowerCase()) {
      case 'legal':
        return 'bg-blue-100 text-blue-800';
      case 'quality':
        return 'bg-emerald-100 text-emerald-800';
      case 'operational':
        return 'bg-amber-100 text-amber-800';
      case 'compliance':
        return 'bg-indigo-100 text-indigo-800';
      case 'declaration':
        return 'bg-slate-100 text-slate-800';
      default:
        return 'bg-slate-100 text-slate-800';
    }
  };

  const isExpiringSoon = (validUntil) => {
    if (!validUntil || validUntil === 'Perpetual' || validUntil === 'TBD' || validUntil === 'Annual Renewal') return false;
    const expiry = new Date(validUntil.split('/').reverse().join('-'));
    const today = new Date();
    const diffTime = expiry - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays <= 30 && diffDays > 0;
  };

  const formatDate = (dateString) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB'); // DD/MM/YYYY format
  };

  // Helper function to fetch digital signature and convert to base64 data URL
  const fetchDigitalSignature = async (vendorId) => {
    try {
      // Fetch all documents for the vendor using documentService
      let documents = [];
      try {
        const response = await documentService.getVendorDocuments(vendorId);
        documents = Array.isArray(response) ? response : (response?.data || response?.documents || []);
      } catch (docError) {
        console.warn('Failed to fetch vendor documents for digital signature:', docError);
        return null;
      }

      // Find the digital signature document
      const digitalSignatureDoc = documents.find(doc => doc.document_type === 'DIGITAL_SIGNATURE');

      if (!digitalSignatureDoc) {
        console.warn('Digital signature document not found for vendor');
        return null;
      }

      // Try authenticated download first, then fallback to public endpoint
      let blob;
      try {
        const downloadResponse = await documentService.downloadDocument(digitalSignatureDoc.id);
        blob = downloadResponse.data;
      } catch (authError) {
        // Fallback to public endpoint
        try {
          const downloadResponse = await fetch(`${API_BASE_URL}/documents/${digitalSignatureDoc.id}/public/download`, {
            credentials: 'include',
          });
          if (!downloadResponse.ok) {
            console.error('Failed to download digital signature:', downloadResponse.status);
            return null;
          }
          blob = await downloadResponse.blob();
        } catch (publicError) {
          console.error('Failed to download digital signature from public endpoint:', publicError);
          return null;
        }
      }

      // Convert to blob then to base64 data URL
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    } catch (error) {
      console.error('Error fetching digital signature:', error);
      return null;
    }
  };

  // Button click handlers
  const handleViewAgreement = async (agreement) => {
    if (agreement.is_basic) {
      try {
        setLoadingData(true);
        const agreementType = AGREEMENT_TYPE_MAP[agreement.id];
        if (!agreementType) {
          toast.error('Unknown agreement type');
          return;
        }
        // Try stored document first, then backend generate
        let pdfBlob;
        try {
          const docsRes = await documentService.getVendorDocuments(vendor.id);
          const documents = Array.isArray(docsRes) ? docsRes : (docsRes?.data || docsRes?.documents || []);
          const filename = AGREEMENT_FILENAME_MAP[agreement.id];
          const doc = documents.find(d => d.file_name === filename);
          if (doc) {
            const resp = await documentService.downloadDocument(doc.id);
            pdfBlob = resp.data;
          }
        } catch (e) {
          // Ignore - fall back to generate
        }
        if (!pdfBlob) {
          pdfBlob = await documentService.getAgreementPdf(vendor.id, agreementType);
        }
        const url = window.URL.createObjectURL(pdfBlob);
        window.open(url, '_blank');
        setTimeout(() => window.URL.revokeObjectURL(url), 1000);
      } catch (err) {
        console.error('Failed to load agreement PDF:', err);
        toast.error('Failed to load agreement PDF. Please try again.');
      } finally {
        setLoadingData(false);
      }
      return;
    }

    // For detailed agreements, keep the existing modal functionality
    setLoadingData(true);
    try {
      const response = await fetch(`${API_BASE_URL}/vendors/${vendor.id}/agreement-details/${agreement.id}/view`, {
        credentials: 'include', // Include cookies for authentication
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      // Handle different response structures
      const agreementData = data?.data || data;
      setAgreementData(agreementData);
      setViewAgreementModal({ isOpen: true, agreement });
    } catch (err) {
      console.error('Failed to load agreement details:', err);
      const errorMsg = getErrorMessage(err) || 'Failed to load agreement details. Please try again.';
      toast.error(errorMsg);
    } finally {
      setLoadingData(false);
    }
  };

  function toCamelCaseObject(obj) {
    const toCamel = str =>
      str.replace(/_([a-z])/g, (_, char) => char.toUpperCase());

    return Object.fromEntries(
      Object.entries(obj).map(([key, value]) => [toCamel(key), value])
    );
  }

  const handleDownloadPDF = async (agreement) => {
    try {
      setLoadingData(true);
      const agreementType = AGREEMENT_TYPE_MAP[agreement.id];
      if (!agreementType) {
        toast.error('Unknown agreement type');
        return;
      }
      let pdfBlob;
      try {
        const docsRes = await documentService.getVendorDocuments(vendor.id);
        const documents = Array.isArray(docsRes) ? docsRes : (docsRes?.data || docsRes?.documents || []);
        const filename = AGREEMENT_FILENAME_MAP[agreement.id];
        const doc = documents.find(d => d.file_name === filename);
        if (doc) {
          const resp = await documentService.downloadDocument(doc.id);
          pdfBlob = resp.data;
        }
      } catch (e) {
        // Ignore - fall back to generate
      }
      if (!pdfBlob) {
        pdfBlob = await documentService.getAgreementPdf(vendor.id, agreementType);
      }
      const url = window.URL.createObjectURL(pdfBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = AGREEMENT_FILENAME_MAP[agreement.id] || 'agreement.pdf';
      a.click();
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    } catch (err) {
      console.error('Failed to download PDF:', err);
      toast.error('Failed to download PDF. Please try again.');
    } finally {
      setLoadingData(false);
    }
  };

  const handleViewSignature = async (agreement) => {
    setLoadingData(true);
    try {
      let response;
      let data;

      if (agreement.is_basic) {
        // For basic agreements, create signature data from registration info
        data = {
          agreement_id: agreement.id,
          agreement_title: agreement.title,
          signature_details: {
            signed_by: agreement.signed_by || vendor.contact_person_name,
            signed_date: agreement.signed_date || vendor.created_at || vendor.registration_date,
            signature_method: "Digital Registration",
            certificate_authority: "Amber Compliance System",
            signature_verified: true,
            timestamp: vendor.created_at || vendor.registration_date || new Date().toISOString(),
            signature_image_url: null
          },
          vendor_info: {
            company_name: vendor.company_name,
            vendor_code: vendor.vendor_code,
            contact_person: vendor.contact_person_name
          }
        };
      } else {
        // For detailed agreements, fetch from API
        const signatureResponse = await fetch(`${API_BASE_URL}/vendors/${vendor.id}/agreement-details/${agreement.id}/signature`, {
          credentials: 'include', // Include cookies for authentication
        });
        if (!signatureResponse.ok) {
          throw new Error(`HTTP error! status: ${signatureResponse.status}`);
        }
        const responseData = await signatureResponse.json();
        // Handle different response structures
        data = responseData?.data || responseData;
      }

      setSignatureData(data);
      setSignatureModal({ isOpen: true, agreement });
    } catch (err) {
      console.error('Failed to load signature details:', err);
      toast.error('Failed to load signature details. Please try again.');
    } finally {
      setLoadingData(false);
    }
  };

  const handleAddComment = async (agreement) => {
    setCommentModal({ isOpen: true, agreement });
    setNewComment('');

    // Load existing comments
    if (!agreement.is_basic) {
      try {
        const response = await fetch(`${API_BASE_URL}/vendors/${vendor.id}/agreement-details/${agreement.id}/comments`, {
          credentials: 'include', // Include cookies for authentication
        });
        if (response.ok) {
          const data = await response.json();
          // Handle different response structures
          const commentsData = data?.data?.comments || data?.comments || [];
          setComments(Array.isArray(commentsData) ? commentsData : []);
        }
      } catch (err) {
        console.error('Failed to load comments:', err);
        // Don't show error to user - comments are optional
      }
    }
  };

  const handleSubmitComment = async () => {
    if (!newComment.trim()) {
      toast.warning('Please enter a comment.');
      return;
    }

    const agreement = commentModal.agreement;
    if (agreement.is_basic) {
      // For basic agreements, just add to local state
      const comment = {
        id: comments.length + 1,
        text: newComment,
        author: 'Current User',
        timestamp: new Date().toISOString(),
        type: 'general'
      };
      setComments([...comments, comment]);
      setNewComment('');
      toast.success('Comment added successfully!');
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/vendors/${vendor.id}/agreement-details/${agreement.id}/comments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include', // Include cookies for authentication
        body: JSON.stringify({
          comment: newComment,
          author: 'Current User',
          type: 'general'
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      // Handle different response structures
      const newCommentData = data?.data?.comment || data?.comment || {
        id: comments.length + 1,
        text: newComment,
        author: 'Current User',
        timestamp: new Date().toISOString(),
        type: 'general'
      };
      setComments([...comments, newCommentData]);
      setNewComment('');
      toast.success('Comment added successfully!');
    } catch (err) {
      console.error('Failed to add comment:', err);
      const errorMsg = getErrorMessage(err) || 'Failed to add comment. Please try again.';
      toast.error(errorMsg);
    }
  };

  const closeModal = () => {
    setViewAgreementModal({ isOpen: false, agreement: null });
    setSignatureModal({ isOpen: false, agreement: null });
    setCommentModal({ isOpen: false, agreement: null });
    setAgreementData(null);
    setSignatureData(null);
    setComments([]);
    setNewComment('');
  };

  if (loading) {
    return (
      <Card className="border border-slate-200 shadow-sm bg-white">
        <CardContent className="p-6">
          <div className="flex items-center justify-center py-12">
            <div className="flex items-center space-x-2">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-slate-600"></div>
              <span className="text-slate-600">Loading agreements...</span>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="border border-red-200 bg-red-50">
        <CardContent className="p-6">
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <Icon name="AlertCircle" size={48} className="text-red-600 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-slate-900 mb-2">Failed to Load Agreements</h3>
              <p className="text-slate-600 mb-4">{error}</p>
              <Button
                variant="outline"
                onClick={() => window.location.reload()}
                iconName="RefreshCw"
                iconPosition="left"
              >
                Try Again
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  const agreementStats = {
    total: agreements.length,
    signed: agreements.filter(item => item.status === 'Signed').length,
    pending: agreements.filter(item => item.status === 'Pending Signature').length,
    expiring: agreements.filter(item => isExpiringSoon(item.valid_until)).length
  };

  const status = vendor?.agreement_generation_status;
  const isGenerating = status === 'PENDING' || status === 'GENERATING';
  const isFailed = status === 'FAILED';

  const hasSignedAgreements =
    vendor?.nda ||
    vendor?.sqa ||
    vendor?.four_m ||
    vendor?.code_of_conduct ||
    vendor?.compliance_agreement ||
    vendor?.self_declaration ||
    vendor?.overseas_agreement;

  const agreementOptions = isIndia(vendor?.country_origin) ? AGREEMENT_OPTIONS_INDIAN : AGREEMENT_OPTIONS_FOREIGN;

  const toggleAgreementType = (value) => {
    setSelectedAgreementTypes((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
    );
  };

  const handleGenerateAgreements = async () => {
    if (!vendor?.id || selectedAgreementTypes.length === 0 || isGeneratingAgreements) return;
    setIsGeneratingAgreements(true);
    try {
      await vendorService.selectAndGenerateAgreements(vendor.id, {
        agreementTypes: selectedAgreementTypes,
        withSignature: generateWithSignature,
      });
      toast.success('Agreement generation started. Status will update shortly.');
      setShowSelectAgreementsModal(false);
      setSelectedAgreementTypes([]);
      setGenerateWithSignature(true);
      onRefresh?.();
    } catch (err) {
      toast.error(getErrorMessage(err) || 'Failed to regenerate agreements');
    } finally {
      setIsGeneratingAgreements(false);
    }
  };

  return (
    <div className="space-y-5">
      {/* Agreement generation status banners */}
      {isGenerating && (
        <div className="flex items-center justify-between p-4 border border-amber-200 rounded-lg bg-amber-50">
          <div className="flex items-center space-x-3">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-amber-600" />
            <div>
              <p className="font-medium text-amber-900">Agreements are being generated</p>
              <p className="text-sm text-amber-800">This usually takes 30–60 seconds.</p>
            </div>
          </div>
          {onRefresh && (
            <Button variant="outline" size="sm" onClick={onRefresh}>
              Refresh
            </Button>
          )}
        </div>
      )}
      {isFailed && (
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 p-4 border border-red-200 rounded-lg bg-red-50">
          <div className="flex items-center space-x-3">
            <Icon name="AlertCircle" size={20} className="text-red-600 flex-shrink-0" />
            <div>
              <p className="font-medium text-red-900">Agreement generation failed</p>
              <p className="text-sm text-red-800">Regenerate from here instead of re-submitting the form.</p>
            </div>
          </div>
          {onRefresh && (
            <div className="flex flex-wrap gap-2">
              <Button
                variant="default"
                size="sm"
                onClick={async () => {
                  if (!vendor?.id || isRegenerating) return;
                  setIsRegenerating(true);
                  try {
                    await vendorService.regenerateAgreements(vendor.id, true);
                    toast.success('Agreement regeneration started. Status will update shortly.');
                    onRefresh();
                  } catch (err) {
                    toast.error(getErrorMessage(err) || 'Failed to regenerate agreements');
                  } finally {
                    setIsRegenerating(false);
                  }
                }}
                disabled={isRegenerating}
              >
                {isRegenerating ? (
                  <>
                    <Icon name="Loader" size={14} className="animate-spin mr-2" />
                    Regenerating…
                  </>
                ) : (
                  'Regenerate with signature'
                )}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={async () => {
                  if (!vendor?.id || isRegenerating) return;
                  setIsRegenerating(true);
                  try {
                    await vendorService.regenerateAgreements(vendor.id, false);
                    toast.success('Agreement regeneration started. Status will update shortly.');
                    onRefresh();
                  } catch (err) {
                    toast.error(getErrorMessage(err) || 'Failed to regenerate agreements');
                  } finally {
                    setIsRegenerating(false);
                  }
                }}
                disabled={isRegenerating}
              >
                Regenerate without signature
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Agreement Types Info */}
      {agreements.some(ag => ag.is_basic) && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <Icon name="Info" size={20} className="text-blue-600 mt-0.5" />
            <div>
              <h4 className="font-medium text-blue-900 mb-1">Agreement Types</h4>
              <p className="text-sm text-blue-800">
                <span className="inline-block px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 border border-blue-200 mr-2">
                  Registration
                </span>
                agreements were signed during vendor registration. Additional detailed agreements can be added separately.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Regenerate or Generate agreements */}
      {vendor?.id && (hasSignedAgreements || agreements.length > 0 || isFailed) && (
        <div className="flex flex-wrap items-center gap-3 p-4 border border-slate-200 rounded-lg bg-slate-50">
          <span className="text-sm font-medium text-slate-700">Regenerate agreements:</span>
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={async () => {
                if (!vendor?.id || isRegenerating) return;
                setIsRegenerating(true);
                try {
                  await vendorService.regenerateAgreements(vendor.id, true);
                  toast.success('Agreement regeneration started. Status will update shortly.');
                  onRefresh?.();
                } catch (err) {
                  toast.error(getErrorMessage(err) || 'Failed to regenerate agreements');
                } finally {
                  setIsRegenerating(false);
                }
              }}
              disabled={isRegenerating || isGenerating}
            >
              {isRegenerating ? (
                <>
                  <Icon name="Loader" size={14} className="animate-spin mr-2" />
                  Regenerating…
                </>
              ) : (
                'Regenerate with signature'
              )}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={async () => {
                if (!vendor?.id || isRegenerating) return;
                setIsRegenerating(true);
                try {
                  await vendorService.regenerateAgreements(vendor.id, false);
                  toast.success('Agreement regeneration started. Status will update shortly.');
                  onRefresh?.();
                } catch (err) {
                  toast.error(getErrorMessage(err) || 'Failed to regenerate agreements');
                } finally {
                  setIsRegenerating(false);
                }
              }}
              disabled={isRegenerating || isGenerating}
            >
              Regenerate without signature
            </Button>
          </div>
        </div>
      )}
      {/* Generate agreements - when vendor has no signed flags */}
      {vendor?.id && !hasSignedAgreements && (
        <div className="flex flex-wrap items-center gap-3 p-4 border border-slate-200 rounded-lg bg-slate-50">
          <span className="text-sm font-medium text-slate-700">No agreements generated yet.</span>
          <Button
            variant="default"
            size="sm"
            onClick={() => setShowSelectAgreementsModal(true)}
            disabled={isGenerating}
          >
            Generate agreements
          </Button>
        </div>
      )}

      {/* Agreement Overview */}
      <Card className="border border-slate-200 shadow-sm bg-white">
        <CardContent className="p-6">
          <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <Icon name="FileSignature" size={20} className="text-slate-600" />
            Agreement Overview
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center">
              <div className="text-2xl font-semibold text-slate-900 mb-1">{agreementStats.total}</div>
              <div className="text-sm text-slate-600">Total Agreements</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-semibold text-emerald-700 mb-1">{agreementStats.signed}</div>
              <div className="text-sm text-slate-600">Signed</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-semibold text-amber-700 mb-1">{agreementStats.pending}</div>
              <div className="text-sm text-slate-600">Pending</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-semibold text-indigo-700 mb-1">{agreementStats.expiring}</div>
              <div className="text-sm text-slate-600">Expiring Soon</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Agreements List */}
      {agreements.length === 0 ? (
        <Card className="border border-slate-200 shadow-sm bg-white">
          <CardContent className="p-6">
            <div className="text-center py-12">
              <Icon name="FileText" size={48} className="text-slate-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-slate-900 mb-2">No Agreements Found</h3>
              <p className="text-slate-600 mb-4">This vendor doesn't have any agreements yet.</p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {agreements.map((agreement) => (
            <Card key={agreement.id} className="border border-slate-200 shadow-sm bg-white">
              <CardContent className="p-6">
                <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2 flex-wrap">
                          <h4 className="text-lg font-semibold text-slate-900">{agreement.title}</h4>
                          {agreement.is_basic && (
                            <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 border border-blue-200">
                              Registration
                            </span>
                          )}
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getTypeColor(agreement.type)}`}>
                            {agreement.type}
                          </span>
                          <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(agreement.status)}`}>
                            {agreement.status}
                          </span>
                        </div>
                        <p className="text-sm text-slate-600 mb-3">{agreement.description}</p>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
                      {agreement.version && (
                        <div>
                          <label className="text-sm font-medium text-slate-600">Version</label>
                          <p className="text-sm text-slate-900 font-medium">v{agreement.version}</p>
                        </div>
                      )}
                      {agreement.document_size && (
                        <div>
                          <label className="text-sm font-medium text-slate-600">Document Size</label>
                          <p className="text-sm text-slate-900">{agreement.document_size}</p>
                        </div>
                      )}
                      {agreement.last_modified && (
                        <div>
                          <label className="text-sm font-medium text-slate-600">Last Modified</label>
                          <p className="text-sm text-slate-900">{formatDate(agreement.last_modified)}</p>
                        </div>
                      )}
                      {agreement.signed_date && (
                        <div>
                          <label className="text-sm font-medium text-slate-600">Signed Date</label>
                          <p className="text-sm text-slate-900">{formatDate(agreement.signed_date)}</p>
                        </div>
                      )}
                      {agreement.signed_by && (
                        <div>
                          <label className="text-sm font-medium text-slate-600">Signed By</label>
                          <p className="text-sm text-slate-900">{agreement.signed_by}</p>
                        </div>
                      )}
                      <div>
                        <label className="text-sm font-medium text-slate-600">Valid Until</label>
                        <p className={`text-sm font-medium ${isExpiringSoon(agreement.valid_until) ? 'text-amber-700' : 'text-slate-900'
                          }`}>
                          {agreement.valid_until}
                          {isExpiringSoon(agreement.valid_until) && (
                            <span className="ml-1 text-xs">(Soon)</span>
                          )}
                        </p>
                      </div>
                    </div>

                    {/* Agreement Properties */}
                    <div className="flex flex-wrap gap-4 mb-4">
                      {agreement.witness_required && (
                        <div className="flex items-center space-x-2">
                          <Icon name="Users" size={16} className="text-slate-600" />
                          <span className="text-sm text-slate-600">Witness Required</span>
                        </div>
                      )}
                      {agreement.auto_renewal && (
                        <div className="flex items-center space-x-2">
                          <Icon name="RefreshCw" size={16} className="text-slate-600" />
                          <span className="text-sm text-slate-600">Auto Renewal</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex flex-wrap gap-3 pt-4 border-t border-slate-200">
                  <Button
                    variant="outline"
                    size="sm"
                    iconName="Eye"
                    iconPosition="left"
                    onClick={() => handleViewAgreement(agreement)}
                    disabled={loadingData}
                  >
                    {loadingData && viewAgreementModal.agreement?.id === agreement.id ? 'Loading...' : 'View Agreement'}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    iconName="Download"
                    iconPosition="left"
                    onClick={() => handleDownloadPDF(agreement)}
                    disabled={loadingData}
                  >
                    {loadingData ? 'Generating...' : 'Download PDF'}
                  </Button>
                  {agreement.status === 'Pending Signature' && (
                    <Button variant="default" size="sm" iconName="PenTool" iconPosition="left">
                      Sign Agreement
                    </Button>
                  )}

     
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Digital Signature Information */}
      <Card className="border border-slate-200 shadow-sm bg-white">
        <CardContent className="p-6">
          <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <Icon name="Shield" size={20} className="text-slate-600" />
            Digital Signature Information
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-slate-600">Signature Method:</span>
                <span className="font-medium text-slate-900">Digital Certificate</span>
              </div>
            </div>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-slate-600">Signature Verification:</span>
                <div className="flex items-center space-x-2">
                  <Icon name="CheckCircle" size={16} className="text-emerald-600" />
                  <span className="font-medium text-emerald-700">Verified</span>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* View Agreement Modal */}
      {viewAgreementModal.isOpen && (
        <>
          <div className="fixed inset-0 bg-black/50 z-50" onClick={closeModal} />
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <div className="bg-surface border border-border rounded-lg shadow-medium w-full max-w-4xl max-h-[calc(90vh/var(--app-scale))] overflow-hidden">
                <div className="flex items-center justify-between p-6 border-b border-border">
                  <div>
                    <h2 className="text-xl font-semibold text-foreground">
                      {viewAgreementModal.agreement?.title}
                    </h2>
                    <div className="text-sm text-text-secondary mt-1">
                      {viewAgreementModal.agreement?.is_basic ? 'Registration Agreement' : 'Detailed Agreement'}
                    </div>
                  </div>
                  <Button variant="ghost" size="icon" onClick={closeModal}>
                    <Icon name="X" size={20} />
                  </Button>
                </div>
                <div className="p-6 overflow-y-auto max-h-[calc(60vh/var(--app-scale))]">
                  {viewAgreementModal.agreement?.is_basic ? (
                    <div className="space-y-4">
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <div className="flex items-start space-x-3">
                          <Icon name="Info" size={20} className="text-blue-600 mt-0.5" />
                          <div>
                            <h4 className="font-medium text-blue-900 mb-1">Registration Agreement</h4>
                            <p className="text-sm text-blue-800">
                              This agreement was signed during vendor registration. The full agreement terms are part of the registration process.
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="text-sm font-medium text-text-secondary">Agreement Type</label>
                          <p className="text-foreground mt-1">{viewAgreementModal.agreement?.type}</p>
                        </div>
                        <div>
                          <label className="text-sm font-medium text-text-secondary">Status</label>
                          <p className="text-foreground mt-1">{viewAgreementModal.agreement?.status}</p>
                        </div>
                        <div>
                          <label className="text-sm font-medium text-text-secondary">Signed Date</label>
                          <p className="text-foreground mt-1">{formatDate(viewAgreementModal.agreement?.signed_date)}</p>
                        </div>
                        <div>
                          <label className="text-sm font-medium text-text-secondary">Signed By</label>
                          <p className="text-foreground mt-1">{viewAgreementModal.agreement?.signed_by}</p>
                        </div>
                        <div>
                          <label className="text-sm font-medium text-text-secondary">Valid Until</label>
                          <p className="text-foreground mt-1">{viewAgreementModal.agreement?.valid_until}</p>
                        </div>
                        <div>
                          <label className="text-sm font-medium text-text-secondary">Description</label>
                          <p className="text-foreground mt-1">{viewAgreementModal.agreement?.description}</p>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {agreementData ? (
                        <>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                            <div>
                              <label className="text-sm font-medium text-text-secondary">Agreement Type</label>
                              <p className="text-foreground mt-1">{agreementData.type}</p>
                            </div>
                            <div>
                              <label className="text-sm font-medium text-text-secondary">Status</label>
                              <p className="text-foreground mt-1">{agreementData.status}</p>
                            </div>
                            <div>
                              <label className="text-sm font-medium text-text-secondary">Version</label>
                              <p className="text-foreground mt-1">{agreementData.version}</p>
                            </div>
                            <div>
                              <label className="text-sm font-medium text-text-secondary">Document Size</label>
                              <p className="text-foreground mt-1">{agreementData.document_size}</p>
                            </div>
                            <div>
                              <label className="text-sm font-medium text-text-secondary">Signed Date</label>
                              <p className="text-foreground mt-1">{formatDate(agreementData.signed_date)}</p>
                            </div>
                            <div>
                              <label className="text-sm font-medium text-text-secondary">Signed By</label>
                              <p className="text-foreground mt-1">{agreementData.signed_by}</p>
                            </div>
                            <div>
                              <label className="text-sm font-medium text-text-secondary">Valid Until</label>
                              <p className="text-foreground mt-1">{agreementData.valid_until}</p>
                            </div>
                            <div>
                              <label className="text-sm font-medium text-text-secondary">Last Modified</label>
                              <p className="text-foreground mt-1">{formatDate(agreementData.last_modified)}</p>
                            </div>
                          </div>
                          <div>
                            <label className="text-sm font-medium text-text-secondary">Description</label>
                            <p className="text-foreground mt-1">{agreementData.description}</p>
                          </div>
                          <div>
                            <label className="text-sm font-medium text-text-secondary">Document Content</label>
                            <div className="mt-2 p-4 bg-muted rounded-lg border">
                              <p className="text-sm text-foreground whitespace-pre-wrap">{agreementData.document_content}</p>
                            </div>
                          </div>
                        </>
                      ) : (
                        <div className="flex items-center justify-center py-8">
                          <Icon name="Loader2" size={32} className="animate-spin text-primary" />
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <div className="flex items-center justify-end space-x-3 p-6 border-t border-border">
                  <Button variant="outline" onClick={closeModal}>
                    Close
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Signature Modal */}
      {signatureModal.isOpen && (
        <>
          <div className="fixed inset-0 bg-black/50 z-50" onClick={closeModal} />
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <div className="bg-surface border border-border rounded-lg shadow-medium w-full max-w-2xl max-h-[calc(90vh/var(--app-scale))] overflow-hidden">
                <div className="flex items-center justify-between p-6 border-b border-border">
                  <div>
                    <h2 className="text-xl font-semibold text-foreground">
                      Signature Details
                    </h2>
                    <div className="text-sm text-text-secondary mt-1">
                      {signatureModal.agreement?.title}
                    </div>
                  </div>
                  <Button variant="ghost" size="icon" onClick={closeModal}>
                    <Icon name="X" size={20} />
                  </Button>
                </div>
                <div className="p-6 overflow-y-auto max-h-[calc(60vh/var(--app-scale))]">
                  {signatureData ? (
                    <div className="space-y-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="text-sm font-medium text-text-secondary">Signed By</label>
                          <p className="text-foreground mt-1">{signatureData.signature_details.signed_by}</p>
                        </div>
                        <div>
                          <label className="text-sm font-medium text-text-secondary">Signed Date</label>
                          <p className="text-foreground mt-1">{formatDate(signatureData.signature_details.signed_date)}</p>
                        </div>
                        <div>
                          <label className="text-sm font-medium text-text-secondary">Signature Method</label>
                          <p className="text-foreground mt-1">{signatureData.signature_details.signature_method}</p>
                        </div>
                        <div>
                          <label className="text-sm font-medium text-text-secondary">Certificate Authority</label>
                          <p className="text-foreground mt-1">{signatureData.signature_details.certificate_authority}</p>
                        </div>
                        <div>
                          <label className="text-sm font-medium text-text-secondary">Verification Status</label>
                          <div className="flex items-center space-x-2 mt-1">
                            <Icon name="CheckCircle" size={16} className="text-success" />
                            <span className="text-success font-medium">Verified</span>
                          </div>
                        </div>
                        <div>
                          <label className="text-sm font-medium text-text-secondary">Timestamp</label>
                          <p className="text-foreground mt-1">{formatDate(signatureData.signature_details.timestamp)}</p>
                        </div>
                      </div>
                      <div className="bg-muted rounded-lg p-4">
                        <div className="flex items-center space-x-2 mb-2">
                          <Icon name="FileSignature" size={20} className="text-primary" />
                          <h4 className="font-medium text-foreground">Digital Signature</h4>
                        </div>
                        <p className="text-sm text-text-secondary">
                          This agreement has been digitally signed using a secure certificate from {signatureData.signature_details.certificate_authority}.
                          The signature has been verified and is legally binding.
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center py-8">
                      <Icon name="Loader2" size={32} className="animate-spin text-primary" />
                    </div>
                  )}
                </div>
                <div className="flex items-center justify-end space-x-3 p-6 border-t border-border">
                  <Button variant="outline" onClick={closeModal}>
                    Close
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Comment Modal */}
      

      {/* Select Agreements to Generate Modal */}
      {showSelectAgreementsModal && (
        <>
          <div className="fixed inset-0 bg-black/60 z-[100]" onClick={() => setShowSelectAgreementsModal(false)} aria-hidden="true" />
          <div className="fixed inset-0 z-[101] flex min-h-full items-center justify-center p-4 overflow-y-auto pointer-events-none">
            <div className="pointer-events-auto bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg shadow-xl w-full max-w-md overflow-hidden">
                <div className="p-6 border-b border-border">
                  <h2 className="text-xl font-semibold text-foreground">Select Agreements to Generate</h2>
                  <p className="text-sm text-text-secondary mt-2">
                    Choose which agreements to generate for this vendor. Selected agreements will be marked as signed and PDFs will be created.
                  </p>
                </div>
                <div className="p-6 space-y-4">
                  {agreementOptions.map((opt) => (
                    <Checkbox
                      key={opt.value}
                      id={`agreement-${opt.value}`}
                      checked={selectedAgreementTypes.includes(opt.value)}
                      onCheckedChange={() => toggleAgreementType(opt.value)}
                      label={opt.label}
                    />
                  ))}
                  <div className="pt-2">
                    <Checkbox
                      id="with-signature"
                      checked={generateWithSignature}
                      onCheckedChange={setGenerateWithSignature}
                      label="Include digital signature"
                    />
                  </div>
                </div>
                <div className="flex items-center justify-end gap-3 p-6 border-t border-border">
                  <Button variant="outline" onClick={() => setShowSelectAgreementsModal(false)}>
                    Cancel
                  </Button>
                  <Button
                    variant="default"
                    onClick={handleGenerateAgreements}
                    disabled={selectedAgreementTypes.length === 0 || isGeneratingAgreements}
                  >
                    {isGeneratingAgreements ? (
                      <>
                        <Icon name="Loader" size={14} className="animate-spin mr-2" />
                        Generating…
                      </>
                    ) : (
                      'Generate'
                    )}
                  </Button>
                </div>
              </div>
          </div>
        </>
      )}
    </div>
  );
};

export default AgreementsTab;